'use client';

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAppSelector } from '@/store/hooks';
import { refreshActiveTable } from '@/store/reducers/table-slice';
import { getUi, hideDrawerState } from '@/store/reducers/ui-slice';
import { BonusPrizeType, BonusReleaseSchedule, BonusType, IBonusDefinitionContent } from '@ap-db-worker/common';
import { Button, Stack } from '@mui/material';
import { Box, Grid } from '@mui/system';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { CustomConfigInputProps, GeneralDrawer, InputType } from '@/types/general';
import { CustomConfigInputV2 } from '@/components/core/custom-config-input-v2';
import { LanguageSelector } from '@/components/core/language-selector';
import { SettingsSection } from '@/components/core/settings-section';
import { UploadCard } from '@/components/core/upload-card';

export default function NewBonusDefinitionContent(): React.JSX.Element | null {
  const searchParams = useSearchParams();
  const dispatch = useDispatch();
  const { selectedMediaItems } = useAppSelector(getUi);
  const selectedOpId = searchParams.get('opId');
  const selectedLanguage = searchParams.get('lang') || 'en';
  const [newBonus, setNewBonus] = useState({
    content: [
      {
        language: 'en',
        title: '',
        description: '',
        intro: '',
        coverPhoto: '',
      },
    ],
    type: BonusType.DEPOSIT_BONUS,
    affId: '',
    createdBy: { id: '', nickname: '', userCode: '' },
    isActive: true,
    opId: '',
    prizeType: BonusPrizeType.CASH_PRIZE,
    bonusStartsAt: new Date(),
    bonusExpiresAt: new Date(),
    parentOpId: '',
    registration: {
      googleSignupPrizeInUSD: 0,
      otherSignupPrizeInUSD: 0,
      telegramSignupPrizeInUSD: 0,
    },
    deposit: {
      multiPaymentGateways: [],
      minDepositAmountInUSD: 1,
      depositPrize: [],
    },
    schedule: BonusReleaseSchedule.EVERY_DAY,
    websiteOrder: 0,
    hide: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const generalFields: CustomConfigInputProps[] = [
    {
      name: 'isActive',
      label: 'Is Active',
      required: false,
      type: InputType.BOOLEAN,
      value: newBonus?.isActive,
      size: { xs: 12, lg: 6 },
      direction: 'row',
      trueLabel: 'Active',
      falseLabel: 'Inactive',
    },
    {
      name: 'prizeType',
      label: 'Prize Type',
      value: newBonus?.prizeType,
      required: true,
      type: InputType.BOOLEAN,
      size: { xs: 12, lg: 6 },
      direction: 'row',
      trueLabel: 'Cash',
      falseLabel: 'Locked',
    },

    {
      name: 'hide',
      label: 'Visibility',
      required: false,
      value: !newBonus?.hide,
      type: InputType.BOOLEAN,
      size: { xs: 12, lg: 6 },
      direction: 'row',
      trueLabel: 'Visible',
      falseLabel: 'Hidden',
    },
    {
      name: 'type',
      label: 'Bonus Type',
      value: newBonus?.type,
      required: true,
      type: InputType.SELECT,
      menuItems: Object.keys(BonusType).map((item) => ({ label: item?.replace(/_/g, ' '), value: item })),
    },

    {
      name: 'bonusStartsAt',
      label: 'Bonus Start Date',
      value: newBonus?.bonusStartsAt,
      required: true,
      type: InputType.DATE_TIME_PICKER,
      size: { xs: 12, lg: 6 },
    },
    {
      name: 'bonusExpiresAt',
      label: 'Bonus Expiry Date',
      value: newBonus?.bonusExpiresAt,
      required: true,
      type: InputType.DATE_TIME_PICKER,
      size: { xs: 12, lg: 6 },
    },

    {
      name: 'schedule',
      label: 'Bonus Release Schedule',
      required: true,
      value: newBonus?.schedule,
      type: InputType.SELECT,
      size: { xs: 12, lg: 6 },
      menuItems: Object.keys(BonusReleaseSchedule).map((item) => ({ label: item?.replace(/_/g, ' '), value: item })),
    },

    {
      name: 'websiteOrder',
      label: 'Website Order',
      required: false,
      type: InputType.NUMBER,
      value: newBonus?.websiteOrder,
    },
  ];
  const prizeFields: CustomConfigInputProps[] = [
    {
      name: 'registration.googleSignupPrizeInUSD',
      label: 'Google Signup Prize (USD)',
      required: false,
      type: InputType.NUMBER,
      hidden: newBonus.type !== BonusType.REGISTRATION_BONUS,
      value: newBonus?.registration?.googleSignupPrizeInUSD,
    },
    {
      name: 'registration.otherSignupPrizeInUSD',
      label: 'Other Signup Prize (USD)',
      required: false,
      type: InputType.NUMBER,
      hidden: newBonus.type !== BonusType.REGISTRATION_BONUS,
      value: newBonus?.registration?.otherSignupPrizeInUSD,
    },
    {
      name: 'registration.telegramSignupPrizeInUSD',
      label: 'Telegram Signup Prize (USD)',
      required: false,
      type: InputType.NUMBER,
      hidden: newBonus.type !== BonusType.REGISTRATION_BONUS,
      value: newBonus?.registration?.telegramSignupPrizeInUSD,
    },
    {
      label: 'Min Deposit Amount In USD',
      name: 'deposit.minDepositAmountInUSD',
      value: newBonus.deposit?.minDepositAmountInUSD,
      type: InputType.NUMBER,
      description: 'Minimum Deposit Amount required',
      hidden: newBonus.type !== BonusType.DEPOSIT_BONUS,
      numberProps: {
        currency: 'USD',
        minAmount: 1,
      },
      required: true,
    },
    {
      label: 'Payment Gateways',
      name: 'deposit.multiPaymentGateways',
      value: newBonus.deposit.multiPaymentGateways,
      type: InputType.AUTO_COMPLETE,
      description: 'Payment Gateways included in this bonus',
      hidden: newBonus.type !== BonusType.DEPOSIT_BONUS,
      menuItems: [],
      required: true,
    },
  ];

  const setNestedField = <T extends object>(obj: T, path: string, value: unknown): void => {
    const keys = path.split('.');
    let current: Record<string, unknown> = obj as Record<string, unknown>;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];

      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }

      current = current[key] as Record<string, unknown>;
    }

    current[keys[keys.length - 1]] = value;
  };

  const handleBonusUiFieldsChange = (
    newValue: string | number | boolean | string[] | number[],
    field: string,
    language: string
  ): void => {
    const updatedContent = { ...newBonus };

    let currentItem: typeof updatedContent | IBonusDefinitionContent = updatedContent;

    if (field !== 'extraData.button.link') {
      const foundItem = updatedContent.content.find((item: IBonusDefinitionContent) => item.language === language);

      if (!foundItem) {
        return;
      }

      currentItem = foundItem;
    }

    setNestedField(currentItem, field, newValue);

    setNewBonus(updatedContent);
  };

  useEffect(() => {
    const languageExists = newBonus.content.find((item) => item.language === selectedLanguage);

    if (!languageExists) {
      setNewBonus((prev) => ({
        ...prev,
        content: [
          ...prev.content,
          { language: selectedLanguage, title: '', description: '', intro: '', coverPhoto: '' },
        ],
      }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    if (selectedMediaItems && selectedMediaItems.target.customKey === 'coverPhoto') {
      handleBonusUiFieldsChange(selectedMediaItems.media?.path || '', 'coverPhoto', selectedLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMediaItems]);

  const submitChanges = async (e: React.FormEvent): Promise<void> => {
    setIsLoading(true);
    e.preventDefault();

    try {
      const response = await fetch(`/api/bonus/definitions?opId=${selectedOpId}`, {
        method: 'post',
        body: JSON.stringify(newBonus),
      });
      const result = await response.json();
      if (response.ok && result.success && result.data) {
        toast.success('Bonus created successfully!');
        dispatch(hideDrawerState(GeneralDrawer.NEW_BONUS_DEFINITION));
        dispatch(refreshActiveTable());
      } else {
        toast.error('Error: 500 | Unable to create new bonus');
      }
    } catch (__) {
      toast.error('Error: 500 | Unable to create new bonus');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={submitChanges}>
      <Stack gap={4}>
        <SettingsSection
          title="General Configuration"
          content={
            <Grid container spacing={4}>
              {generalFields.map((item, index) => (
                <CustomConfigInputV2 key={index} defaultData={newBonus} setDefaultData={setNewBonus} {...item} />
              ))}
            </Grid>
          }
        />
        <SettingsSection
          title="Prize"
          content={
            <Grid container spacing={4}>
              {prizeFields.map((item, index) => (
                <CustomConfigInputV2 key={index} defaultData={newBonus} setDefaultData={setNewBonus} {...item} />
              ))}
            </Grid>
          }
        />
        <SettingsSection
          title="UI Configuration"
          content={
            <Stack gap={2}>
              <LanguageSelector />
              <Grid container spacing={4}>
                {newBonus.content
                  .filter((item) => item.language === selectedLanguage)
                  .map((input, index) => (
                    <React.Fragment key={index}>
                      <Grid size={{ xs: 12 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <UploadCard
                          id={1}
                          key={1}
                          customKey="coverPhoto"
                          image={{ url: input.coverPhoto, alt: '' }}
                          recommendedDimensions="(240 x 40)"
                        />
                      </Grid>
                      <CustomConfigInputV2
                        label={`Title (${selectedLanguage.toUpperCase()})`}
                        name="title"
                        description="Bonus title, will be visible to players in bonus section"
                        type={InputType.INPUT}
                        value={input.title}
                        inputProps={{
                          maxLength: 50,
                        }}
                        onChange={(newValue) => {
                          if (newValue) {
                            handleBonusUiFieldsChange(newValue, 'title', input.language);
                          }
                        }}
                      />
                      <CustomConfigInputV2
                        label={`Description (${selectedLanguage.toUpperCase()})`}
                        name="description"
                        description="Bonus description, will be visible to players in bonus section"
                        type={InputType.INPUT}
                        value={input.description}
                        onChange={(newValue) => {
                          if (newValue) {
                            handleBonusUiFieldsChange(newValue, 'description', input.language);
                          }
                        }}
                      />
                    </React.Fragment>
                  ))}
              </Grid>
            </Stack>
          }
        />

        <Box sx={{ textAlign: 'end', mt: 6 }}>
          <Button loading={isLoading} type="submit" variant="contained">
            Save
          </Button>
        </Box>
      </Stack>
    </form>
  );
}
